import { RESEARCH_TEAM } from "globals/constants/formObjects";

import { deepCopy } from "../utils/helpers";
const { EFFORT_FORMAT } = RESEARCH_TEAM;


export default function useEffortCalculation (updateValue, onChange, index, subIndex, association, configs) {
	const appointmentTypeValues = {
		academic: configs.academic,
		calendar: configs.calendar,
		summer: configs.summer,
	};

	const toFixed = number => {
		return Math.round(number * 100) / 100;
	};

	const percentageToMonths = (percentageValue, maxMonthsValue) => {
		if (percentageValue === null) {
			return null;
		}

		return toFixed((percentageValue / 100) * maxMonthsValue);
	};

	const monthsToPercentage = (monthsValue, maxMonthsValue) => {
		if (monthsValue === null) {
			return null;
		}

		return toFixed((monthsValue / maxMonthsValue) * 100);
	};

	const convertMonthsAndPercentage = (association, effortFormat) => {
		const convertedValues = {
			sponsoredEffort: {},
			sponsoredEffortMonths: {},
			costShareEffort: {},
			costShareEffortMonths: {},
		};
		[ "sponsoredEffort", "costShareEffort" ].forEach(effortField => {
			[ "academic", "summer", "calendar" ].forEach(effortType => {
				if (effortFormat === EFFORT_FORMAT.MONTHS) {
					convertedValues[effortField][effortType] = monthsToPercentage(association[`${effortField}Months`][effortType], appointmentTypeValues[effortType]) ?? null;
					convertedValues[`${effortField}Months`][effortType] = association[`${effortField}Months`][effortType] ?? null;
				} else {
					convertedValues[effortField][effortType] = association[effortField][effortType] ?? null;
					convertedValues[`${effortField}Months`][effortType] = percentageToMonths(association[effortField][effortType], appointmentTypeValues[effortType]) ?? null;
				}
			});
		});

		return convertedValues;
	};

	const calculateTotalEffort = (modifiedAssociation, calculatedEffortValues, effortField, effortFieldMonths, effortType) => {
		const totalEfforts = {
			totalEffort: 0,
			totalEffortMonths: 0,
		};

		const updatedAssociation = modifiedAssociation || deepCopy(association);

		if (calculatedEffortValues) {
			updatedAssociation[effortField][effortType] = calculatedEffortValues[effortField];
			updatedAssociation[effortFieldMonths][effortType] = calculatedEffortValues[effortFieldMonths];
		}

		const A_SE = updatedAssociation.sponsoredEffort?.academic,
			S_SE = updatedAssociation.sponsoredEffort?.summer,
			C_SE = updatedAssociation.sponsoredEffort?.calendar,
			A_CSE = updatedAssociation.costShareEffort?.academic,
			S_CSE = updatedAssociation.costShareEffort?.summer,
			C_CSE = updatedAssociation.costShareEffort?.calendar,
			A_SE_M = updatedAssociation.sponsoredEffortMonths?.academic,
			S_SE_M = updatedAssociation.sponsoredEffortMonths?.summer,
			C_SE_M = updatedAssociation.sponsoredEffortMonths?.calendar,
			A_CSE_M = updatedAssociation.costShareEffortMonths?.academic,
			S_CSE_M = updatedAssociation.costShareEffortMonths?.summer,
			C_CSE_M = updatedAssociation.costShareEffortMonths?.calendar,
			Am = appointmentTypeValues.academic,
			Sm = appointmentTypeValues.summer,
			Cm = appointmentTypeValues.calendar;

		totalEfforts.totalEffort = toFixed(
			(((A_SE + A_CSE) / 100 * Am) +
            ((S_SE + S_CSE) / 100 * Sm) +
            ((C_SE + C_CSE) / 100 * Cm)) /
            (Am + Sm + Cm) * 100
		) ?? 0;

		totalEfforts.totalEffortMonths = toFixed(
			A_SE_M + S_SE_M + C_SE_M + A_CSE_M + S_CSE_M + C_CSE_M
		) ?? 0;

		return totalEfforts;
	};

	const setInitialCalculatedEffortValues = () => {
		const modifiedAssociation = deepCopy(association);

		modifiedAssociation.sponsoredEffortMonths.academic = percentageToMonths(association.sponsoredEffort.academic, appointmentTypeValues.academic);
		modifiedAssociation.sponsoredEffortMonths.summer = percentageToMonths(association.sponsoredEffort.summer, appointmentTypeValues.summer);
		modifiedAssociation.sponsoredEffortMonths.calendar = percentageToMonths(association.sponsoredEffort.calendar, appointmentTypeValues.calendar);
		modifiedAssociation.costShareEffortMonths.academic = percentageToMonths(association.costShareEffort.academic, appointmentTypeValues.academic);
		modifiedAssociation.costShareEffortMonths.summer = percentageToMonths(association.costShareEffort.summer, appointmentTypeValues.summer);
		modifiedAssociation.costShareEffortMonths.calendar = percentageToMonths(association.costShareEffort.calendar, appointmentTypeValues.calendar);

		const calculatedTotalEfforts = calculateTotalEffort(modifiedAssociation);

		updateValue(onChange, (value => {
			value[index].associations[subIndex].sponsoredEffortMonths.academic = modifiedAssociation.sponsoredEffortMonths.academic;
			value[index].associations[subIndex].sponsoredEffortMonths.summer = modifiedAssociation.sponsoredEffortMonths.summer;
			value[index].associations[subIndex].sponsoredEffortMonths.calendar = modifiedAssociation.sponsoredEffortMonths.calendar;
			value[index].associations[subIndex].costShareEffortMonths.academic = modifiedAssociation.costShareEffortMonths.academic;
			value[index].associations[subIndex].costShareEffortMonths.summer = modifiedAssociation.costShareEffortMonths.summer;
			value[index].associations[subIndex].costShareEffortMonths.calendar = modifiedAssociation.costShareEffortMonths.calendar;
			value[index].associations[subIndex].totalEffort = calculatedTotalEfforts.totalEffort;
			value[index].associations[subIndex].totalEffortMonths = calculatedTotalEfforts.totalEffortMonths;
			value[index].isInitialCalculationRequired = false;
		}));
	};

	return [
		setInitialCalculatedEffortValues,
		calculateTotalEffort,
		convertMonthsAndPercentage,
		percentageToMonths,
		monthsToPercentage
	];
}